package com.havn.app.features.openhouses.presentation

import com.havn.app.core.billing.HavnPlanLimits
import com.havn.app.core.billing.HavnPlans
import com.havn.app.core.logging.HavnLogger
import com.havn.app.features.agency.domain.AgencyRepository
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.clients.domain.ClientRepository
import com.havn.app.features.openhouses.domain.KioskSession
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.domain.OpenHouseDraft
import com.havn.app.features.openhouses.domain.OpenHousePropertyOption
import com.havn.app.features.openhouses.domain.OpenHouseRepository
import com.havn.app.features.openhouses.domain.VisitorInterest
import com.havn.app.features.openhouses.domain.VisitorSignInDraft
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.random.Random

class OpenHousesViewModel(
    private val openHouseRepository: OpenHouseRepository,
    private val clientRepository: ClientRepository,
    private val authRepository: AuthRepository,
    private val agencyRepository: AgencyRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(OpenHousesUiState())
    val uiState: StateFlow<OpenHousesUiState> = _uiState.asStateFlow()

    private var agentId: String? = null
    private var agentEmail: String? = null
    private var planLimits: HavnPlanLimits = HavnPlans.freeLimits
    private var onOpenHouseScheduled: (() -> Unit)? = null
    private var selectedAgencyId: String? = null
    private var hasLoadedAgencyContext = false

    init {
        scope.launch {
            authRepository.authState.collect { state ->
                val user = (state as? AuthState.LoggedIn)?.user
                agentId = user?.id
                agentEmail = user?.email
                if (user == null) {
                    hasLoadedAgencyContext = false
                    _uiState.value = OpenHousesUiState()
                } else {
                    openHouseRepository.ensureAgentProfile(user.id, user.email)
                        .onFailure { setError(it) }
                    loadPlanLimits(user.id)
                    if (hasLoadedAgencyContext) {
                        load(user.id)
                    }
                }
            }
        }
    }

    fun refresh() {
        val currentAgentId = agentId ?: return
        scope.launch { load(currentAgentId) }
    }

    fun updateSearchQuery(query: String) {
        if (_uiState.value.searchQuery == query) return
        _uiState.value = _uiState.value.copy(searchQuery = query, error = null)
        val currentAgentId = agentId ?: return
        scope.launch { load(currentAgentId) }
    }

    fun loadMoreOpenHouses() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.isLoading || state.isLoadingMore || !state.hasMoreOpenHouses) return
        scope.launch { load(currentAgentId, append = true) }
    }

    fun loadOpenHousesForProperty(propertyId: String) {
        val currentAgentId = agentId ?: return
        scope.launch {
            openHouseRepository.openHousesForProperty(currentAgentId, propertyId)
                .onSuccess { propertyOpenings ->
                    mergeOpenHouses(propertyOpenings)
                }
                .onFailure { setError(it) }
        }
    }

    fun setAgencyContext(agencyId: String?) {
        if (hasLoadedAgencyContext && selectedAgencyId == agencyId) return
        selectedAgencyId = agencyId
        hasLoadedAgencyContext = true
        val currentAgentId = agentId ?: return
        scope.launch { load(currentAgentId) }
    }

    fun onOpenHouseScheduled(callback: () -> Unit) {
        onOpenHouseScheduled = callback
    }

    fun showList() {
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.List,
            selectedOpenHouseId = null,
            returnToPropertyId = null
        )
    }

    fun showSchedule() {
        val state = _uiState.value
        val firstPropertyId = state.propertyOptions.firstOrNull { property ->
            state.openingCountForProperty(property.id) < planLimits.maxOpeningsPerProperty
        }?.id
        if (firstPropertyId == null) {
            _uiState.value = state.copy(error = "Your current plan includes up to ${planLimits.maxOpeningsPerProperty} openings per property.")
            return
        }
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.Schedule,
            selectedOpenHouseId = null,
            returnToPropertyId = null,
            form = OpenHouseFormState.defaultFor(firstPropertyId)
        )
    }

    fun showScheduleForProperty(propertyId: String) {
        val state = _uiState.value
        if (state.openingCountForProperty(propertyId) >= planLimits.maxOpeningsPerProperty) {
            _uiState.value = state.copy(error = "Your current plan includes up to ${planLimits.maxOpeningsPerProperty} openings for each property.")
            return
        }
        _uiState.value = state.copy(
            mode = OpenHousesMode.Schedule,
            selectedOpenHouseId = null,
            returnToPropertyId = propertyId,
            form = OpenHouseFormState.defaultFor(propertyId)
        )
    }

    fun clearPropertyReturn() {
        _uiState.value = _uiState.value.copy(returnToPropertyId = null)
    }

    fun openDetail(openHouseId: String) {
        val currentAgentId = agentId ?: return
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.Detail,
            selectedOpenHouseId = openHouseId,
            isDetailLoading = true,
            error = null
        )
        scope.launch {
            openHouseRepository.openHouseDetail(currentAgentId, openHouseId)
                .onSuccess {
                    replaceOpenHouse(it)
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isDetailLoading = false)
                    setError(it)
                }
        }
    }

    fun showExistingDetail(openHouseId: String) {
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.Detail,
            selectedOpenHouseId = openHouseId,
            isDetailLoading = false,
            error = null
        )
    }

    fun showLiveDashboard(openHouseId: String) {
        _uiState.value = _uiState.value.copy(mode = OpenHousesMode.LiveDashboard, selectedOpenHouseId = openHouseId)
        openDetail(openHouseId)
        _uiState.value = _uiState.value.copy(mode = OpenHousesMode.LiveDashboard, selectedOpenHouseId = openHouseId)
    }

    fun updateForm(transform: OpenHouseFormState.() -> OpenHouseFormState) {
        _uiState.value = _uiState.value.copy(form = _uiState.value.form.transform(), error = null)
    }

    fun scheduleOpenHouse() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.openingCountForProperty(state.form.propertyId) >= planLimits.maxOpeningsPerProperty) {
            _uiState.value = state.copy(
                isSaving = false,
                error = "Your current plan includes up to ${planLimits.maxOpeningsPerProperty} openings for each property."
            )
            return
        }
        val validation = state.form.validate()
        if (validation != null) {
            _uiState.value = state.copy(form = state.form.copy(validationError = validation))
            return
        }

        _uiState.value = state.copy(isSaving = true, error = null)
        scope.launch {
            openHouseRepository.scheduleOpenHouse(currentAgentId, state.form.toDraft(), selectedAgencyId)
                .onSuccess { openHouse ->
                    onOpenHouseScheduled?.invoke()
                    replaceOpenHouse(openHouse)
                    _uiState.value = _uiState.value.copy(
                        mode = OpenHousesMode.Detail,
                        selectedOpenHouseId = openHouse.id,
                        isSaving = false,
                        form = OpenHouseFormState()
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun startEdit() {
        val selected = _uiState.value.selectedOpenHouse ?: return
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.Edit,
            form = selected.toFormState(),
            error = null
        )
    }

    fun cancelForm() {
        val selectedId = _uiState.value.selectedOpenHouseId
        _uiState.value = _uiState.value.copy(
            mode = if (selectedId == null) OpenHousesMode.List else OpenHousesMode.Detail,
            form = OpenHouseFormState(),
            error = null
        )
    }

    fun saveEditedOpenHouse() {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        val state = _uiState.value
        val validation = state.form.validate()
        if (validation != null) {
            _uiState.value = state.copy(form = state.form.copy(validationError = validation))
            return
        }

        _uiState.value = state.copy(isSaving = true, error = null)
        scope.launch {
            openHouseRepository.updateOpenHouse(currentAgentId, openHouseId, state.form.toDraft())
                .onSuccess { openHouse ->
                    replaceOpenHouse(openHouse)
                    _uiState.value = _uiState.value.copy(
                        mode = OpenHousesMode.Detail,
                        selectedOpenHouseId = openHouse.id,
                        isSaving = false,
                        form = OpenHouseFormState()
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun requestDeleteSelected() {
        if (_uiState.value.selectedOpenHouseId == null) return
        _uiState.value = _uiState.value.copy(showDeleteConfirmation = true, error = null)
    }

    fun dismissDeleteConfirmation() {
        _uiState.value = _uiState.value.copy(showDeleteConfirmation = false)
    }

    fun confirmDeleteSelected() {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, showDeleteConfirmation = false, error = null)
        scope.launch {
            openHouseRepository.deleteOpenHouse(currentAgentId, openHouseId)
                .onSuccess {
                    _uiState.value = _uiState.value.copy(
                        openHouses = _uiState.value.openHouses.filterNot { it.id == openHouseId },
                        selectedOpenHouseId = null,
                        mode = OpenHousesMode.List,
                        isSaving = false,
                        form = OpenHouseFormState()
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    fun startSelectedOpenHouse() {
        updateSelectedStatus(start = true)
    }

    fun startOpenHouse(openHouseId: String) {
        _uiState.value = _uiState.value.copy(selectedOpenHouseId = openHouseId)
        updateSelectedStatus(start = true)
    }

    fun endSelectedOpenHouse() {
        updateSelectedStatus(start = false)
    }

    fun generateKioskSession() {
        val selected = _uiState.value.selectedOpenHouse ?: return
        val code = List(6) { Random.nextInt(0, 10) }.joinToString("")
        _uiState.value = _uiState.value.copy(
            kioskSession = KioskSession(
                openHouseId = selected.id,
                code = code,
                kioskPath = "havn://kiosk/open-house/${selected.id}?code=$code"
            )
        )
    }

    fun launchKioskMode() {
        val selected = _uiState.value.selectedOpenHouse ?: return
        val currentSession = _uiState.value.kioskSession?.takeIf { it.openHouseId == selected.id }
        if (currentSession == null) generateKioskSession()
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.VisitorKiosk,
            kioskStep = KioskStep.Welcome,
            visitorForm = VisitorKioskFormState(),
            kioskError = null
        )
    }

    fun showQrScanner() {
        val selected = _uiState.value.selectedOpenHouse ?: return
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.ScanQr,
            selectedOpenHouseId = selected.id,
            qrScanPayload = "",
            qrScanPayloads = emptyList(),
            qrScanMessage = null,
            clientSearchQuery = "",
            kioskError = null,
            error = null
        )
    }

    fun showClientSelector() {
        val selected = _uiState.value.selectedOpenHouse ?: return
        _uiState.value = _uiState.value.copy(
            mode = OpenHousesMode.SelectClients,
            selectedOpenHouseId = selected.id,
            clientSearchQuery = "",
            qrScanMessage = null,
            kioskError = null,
            error = null
        )
    }

    fun updateQrScanPayload(value: String) {
        _uiState.value = _uiState.value.copy(qrScanPayload = value, qrScanMessage = null, kioskError = null, error = null)
    }

    fun submitQrScan() {
        val payloads = _uiState.value.qrScanPayloads.ifEmpty {
            listOfNotNull(_uiState.value.qrScanPayload.takeIf { it.isNotBlank() })
        }
        submitQrPayloads(payloads)
    }

    fun submitScannedQrPayload(payload: String) {
        if (payload.isBlank()) return
        val state = _uiState.value
        if (payload in state.qrScanPayloads) {
            _uiState.value = state.copy(qrScanMessage = "Contact QR already scanned.", kioskError = null, error = null)
            return
        }
        _uiState.value = state.copy(
            qrScanPayload = payload,
            qrScanPayloads = state.qrScanPayloads + payload,
            qrScanMessage = "Contact QR scanned. Tap Add QR Contact when ready.",
            kioskError = null,
            error = null
        )
    }

    fun checkInScannedQrPayload(payload: String) {
        submitQrPayloads(listOf(payload))
    }

    fun removeScannedQrPayload(payload: String) {
        _uiState.value = _uiState.value.copy(
            qrScanPayloads = _uiState.value.qrScanPayloads.filterNot { it == payload },
            qrScanMessage = null,
            kioskError = null,
            error = null
        )
    }

    private fun submitQrPayloads(payloads: List<String>) {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        val uniquePayloads = payloads.map { it.trim() }.filter { it.isNotBlank() }.distinct()
        if (uniquePayloads.isEmpty()) {
            _uiState.value = _uiState.value.copy(kioskError = "Scan a HAVN contact QR first.")
            return
        }

        _uiState.value = _uiState.value.copy(isSaving = true, kioskError = null, qrScanMessage = null)
        scope.launch {
            var latestOpenHouse: OpenHouse? = null
            var createdCount = 0
            var firstError: Throwable? = null
            for (payload in uniquePayloads) {
                openHouseRepository.checkInClientQr(currentAgentId, openHouseId, payload)
                    .onSuccess { openHouse ->
                        latestOpenHouse = openHouse
                        createdCount += 1
                    }
                    .onFailure {
                        firstError = it
                    }
                if (firstError != null) break
            }
            latestOpenHouse?.let { replaceOpenHouse(it) }
            if (firstError == null) {
                _uiState.value = _uiState.value.copy(
                    isSaving = false,
                    qrScanPayload = "",
                    qrScanPayloads = emptyList(),
                    qrScanMessage = "$createdCount QR contact${if (createdCount == 1) "" else "s"} added as lead${if (createdCount == 1) "" else "s"}."
                )
            } else {
                _uiState.value = _uiState.value.copy(isSaving = false)
                setError(firstError)
                _uiState.value = _uiState.value.copy(kioskError = _uiState.value.error)
            }
        }
    }

    fun updateClientSearchQuery(value: String) {
        _uiState.value = _uiState.value.copy(clientSearchQuery = value)
    }

    fun checkInClient(clientId: String) {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        val client = _uiState.value.clients.firstOrNull { it.id == clientId } ?: return

        _uiState.value = _uiState.value.copy(isSaving = true, kioskError = null, qrScanMessage = null)
        scope.launch {
            openHouseRepository.checkInClientQr(currentAgentId, openHouseId, client.qrPayload)
                .onSuccess { openHouse ->
                    replaceOpenHouse(openHouse)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        qrScanMessage = "${client.fullName} checked in and lead created.",
                        clientSearchQuery = ""
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                    _uiState.value = _uiState.value.copy(kioskError = _uiState.value.error)
                }
        }
    }

    fun removeClient(clientId: String) {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        val client = _uiState.value.clients.firstOrNull { it.id == clientId }

        _uiState.value = _uiState.value.copy(isSaving = true, kioskError = null, qrScanMessage = null)
        scope.launch {
            openHouseRepository.removeClientFromOpenHouse(currentAgentId, openHouseId, clientId)
                .onSuccess { openHouse ->
                    replaceOpenHouse(openHouse)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        qrScanMessage = "${client?.fullName ?: "Contact"} removed from opening."
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                    _uiState.value = _uiState.value.copy(kioskError = _uiState.value.error)
                }
        }
    }

    fun exitKioskMode() {
        val selectedId = _uiState.value.selectedOpenHouseId
        _uiState.value = _uiState.value.copy(
            mode = if (selectedId == null) OpenHousesMode.List else OpenHousesMode.Detail,
            kioskStep = KioskStep.Welcome,
            visitorForm = VisitorKioskFormState(),
            kioskError = null
        )
    }

    fun updateVisitorForm(transform: VisitorKioskFormState.() -> VisitorKioskFormState) {
        _uiState.value = _uiState.value.copy(
            visitorForm = _uiState.value.visitorForm.transform(),
            kioskError = null
        )
    }

    fun nextKioskStep() {
        val state = _uiState.value
        val next = when (state.kioskStep) {
            KioskStep.Welcome -> KioskStep.Contact
            KioskStep.Contact -> {
                val validation = state.visitorForm.validateContact()
                if (validation != null) {
                    _uiState.value = state.copy(kioskError = validation)
                    return
                }
                KioskStep.Questions
            }
            KioskStep.Questions -> KioskStep.Questions
            KioskStep.ThankYou -> KioskStep.Welcome
        }
        _uiState.value = state.copy(kioskStep = next, kioskError = null)
    }

    fun previousKioskStep() {
        val previous = when (_uiState.value.kioskStep) {
            KioskStep.Welcome -> KioskStep.Welcome
            KioskStep.Contact -> KioskStep.Welcome
            KioskStep.Questions -> KioskStep.Contact
            KioskStep.ThankYou -> KioskStep.Welcome
        }
        _uiState.value = _uiState.value.copy(kioskStep = previous, kioskError = null)
    }

    fun submitVisitorSignIn() {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        val state = _uiState.value
        val validation = state.visitorForm.validateQuestions()
        if (validation != null) {
            _uiState.value = state.copy(kioskError = validation)
            return
        }

        _uiState.value = state.copy(isSaving = true, kioskError = null)
        scope.launch {
            openHouseRepository.submitVisitorSignIn(
                agentId = currentAgentId,
                openHouseId = openHouseId,
                draft = state.visitorForm.toDraft()
            ).onSuccess { openHouse ->
                replaceOpenHouse(openHouse)
                _uiState.value = _uiState.value.copy(
                    isSaving = false,
                    kioskStep = KioskStep.ThankYou,
                    visitorForm = VisitorKioskFormState()
                )
                delay(5_000)
                if (_uiState.value.mode == OpenHousesMode.VisitorKiosk && _uiState.value.kioskStep == KioskStep.ThankYou) {
                    _uiState.value = _uiState.value.copy(kioskStep = KioskStep.Welcome)
                }
            }.onFailure {
                _uiState.value = _uiState.value.copy(isSaving = false)
                setError(it)
                _uiState.value = _uiState.value.copy(kioskError = _uiState.value.error)
            }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    fun dispose() {
        scope.cancel()
    }

    private fun updateSelectedStatus(start: Boolean) {
        val currentAgentId = agentId ?: return
        val openHouseId = _uiState.value.selectedOpenHouseId ?: return
        _uiState.value = _uiState.value.copy(isSaving = true, error = null)
        scope.launch {
            val result = if (start) {
                openHouseRepository.startOpenHouse(currentAgentId, openHouseId)
            } else {
                openHouseRepository.endOpenHouse(currentAgentId, openHouseId)
            }
            result
                .onSuccess {
                    replaceOpenHouse(it)
                    _uiState.value = _uiState.value.copy(
                        mode = if (start) OpenHousesMode.LiveDashboard else OpenHousesMode.Detail,
                        selectedOpenHouseId = it.id,
                        isSaving = false
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false)
                    setError(it)
                }
        }
    }

    private suspend fun load(currentAgentId: String, append: Boolean = false) {
        val state = _uiState.value
        val offset = if (append) state.openHouses.size else 0
        _uiState.value = if (append) {
            state.copy(isLoadingMore = true, error = null)
        } else {
            state.copy(isLoading = true, error = null)
        }
        val openHouses = openHouseRepository.openHousePage(
            agentId = currentAgentId,
            agencyId = selectedAgencyId,
            query = _uiState.value.searchQuery,
            offset = offset,
            limit = OPEN_HOUSE_PAGE_SIZE
        )
        openHouses.onFailure { setError(it) }

        val visibleOpenHouses = openHouses.getOrElse { emptyList() }

        _uiState.value = _uiState.value.copy(
            isLoading = false,
            isLoadingMore = false,
            openHouses = if (append) (_uiState.value.openHouses + visibleOpenHouses).distinctBy { it.id } else visibleOpenHouses,
            hasMoreOpenHouses = visibleOpenHouses.size == OPEN_HOUSE_PAGE_SIZE
        )

        val properties = openHouseRepository.propertyOptions(currentAgentId, selectedAgencyId)
        properties.onSuccess { propertyOptions ->
            _uiState.value = _uiState.value.copy(
                propertyOptions = propertyOptions.take(planLimits.visibleProperties)
            )
        }.onFailure { setError(it) }

        val clients = clientRepository.clientsForAgent(currentAgentId, selectedAgencyId)
        clients.onSuccess { loadedClients ->
            _uiState.value = _uiState.value.copy(clients = loadedClients)
        }.onFailure { setError(it) }
    }

    private suspend fun loadPlanLimits(currentAgentId: String) {
        agencyRepository.entitlementForUser(currentAgentId)
            .onSuccess { entitlement ->
                planLimits = HavnPlans.limitsFor(entitlement.tier)
            }
            .onFailure {
                HavnLogger.error("OpenHousesViewModel", "Failed to load plan limits", it)
                planLimits = HavnPlans.freeLimits
            }
    }

    private fun replaceOpenHouse(openHouse: OpenHouse) {
        mergeOpenHouses(listOf(openHouse))
    }

    private fun mergeOpenHouses(openHouses: List<OpenHouse>) {
        val current = _uiState.value.openHouses
        val next = (openHouses + current).distinctBy { it.id }
            .sortedByDescending { it.startsAt }
        _uiState.value = _uiState.value.copy(openHouses = next)
    }

    private fun setError(error: Throwable) {
        HavnLogger.error("OpenHousesViewModel", "User-visible open-house error", error)
        _uiState.value = _uiState.value.copy(
            error = error.message?.takeIf { it.isNotBlank() } ?: "Something went wrong."
        )
    }
}

private fun OpenHousesUiState.openingCountForProperty(propertyId: String): Int =
    openHouses.count { it.propertyId == propertyId }

data class OpenHousesUiState(
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    val isDetailLoading: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
    val mode: OpenHousesMode = OpenHousesMode.List,
    val openHouses: List<OpenHouse> = emptyList(),
    val searchQuery: String = "",
    val hasMoreOpenHouses: Boolean = false,
    val propertyOptions: List<OpenHousePropertyOption> = emptyList(),
    val clients: List<Client> = emptyList(),
    val selectedOpenHouseId: String? = null,
    val form: OpenHouseFormState = OpenHouseFormState(),
    val kioskSession: KioskSession? = null,
    val kioskStep: KioskStep = KioskStep.Welcome,
    val visitorForm: VisitorKioskFormState = VisitorKioskFormState(),
    val kioskError: String? = null,
    val qrScanPayload: String = "",
    val qrScanPayloads: List<String> = emptyList(),
    val qrScanMessage: String? = null,
    val clientSearchQuery: String = "",
    val showDeleteConfirmation: Boolean = false,
    val returnToPropertyId: String? = null
) {
    val selectedOpenHouse: OpenHouse?
        get() = openHouses.firstOrNull { it.id == selectedOpenHouseId }

    val liveCount: Int
        get() = openHouses.count { it.status.databaseValue == "live" }

    val scheduledCount: Int
        get() = openHouses.count { it.status.databaseValue == "scheduled" }

    val visitorCount: Int
        get() = openHouses.sumOf { it.stats.visitorCount }

    val filteredClients: List<Client>
        get() {
            val query = clientSearchQuery.trim()
            return clients.filter { client ->
                query.isBlank() ||
                    client.fullName.contains(query, ignoreCase = true) ||
                    client.email.orEmpty().contains(query, ignoreCase = true) ||
                    client.phone.orEmpty().contains(query, ignoreCase = true)
            }
        }
}

enum class OpenHousesMode {
    List,
    Detail,
    Schedule,
    Edit,
    LiveDashboard,
    VisitorKiosk,
    ScanQr,
    SelectClients
}

private fun OpenHouse.toFormState(): OpenHouseFormState = OpenHouseFormState(
    propertyId = propertyId,
    title = title.orEmpty(),
    startsAt = startsAt,
    endsAt = endsAt,
    accessNotes = accessNotes.orEmpty(),
    internalNotes = internalNotes.orEmpty()
)

private const val OPEN_HOUSE_PAGE_SIZE = 20

enum class KioskStep {
    Welcome,
    Contact,
    Questions,
    ThankYou
}

data class OpenHouseFormState(
    val propertyId: String = "",
    val title: String = "",
    val startsAt: String = "",
    val endsAt: String = "",
    val accessNotes: String = "",
    val internalNotes: String = "",
    val validationError: String? = null
) {
    fun toDraft(): OpenHouseDraft = OpenHouseDraft(
        propertyId = propertyId,
        title = title,
        startsAt = startsAt,
        endsAt = endsAt,
        accessNotes = accessNotes,
        internalNotes = internalNotes
    )

    fun validate(): String? = when {
        propertyId.isBlank() -> "Choose a property."
        startsAt.isBlank() -> "Start time is required."
        endsAt.isBlank() -> "End time is required."
        else -> null
    }

    companion object {
        fun defaultFor(propertyId: String): OpenHouseFormState {
            val date = Clock.System.now().toString().substringBefore("T")
            return OpenHouseFormState(
                propertyId = propertyId,
                startsAt = "${date}T16:00:00Z",
                endsAt = "${date}T18:00:00Z"
            )
        }
    }
}

data class VisitorKioskFormState(
    val fullName: String = "",
    val phone: String = "",
    val email: String = "",
    val workingWithAgent: Boolean? = null,
    val interest: VisitorInterest? = null
) {
    fun validateContact(): String? = when {
        fullName.isBlank() -> "Please enter your name."
        phone.isBlank() && email.isBlank() -> "Please enter a phone number or email."
        email.isNotBlank() && ("@" !in email || "." !in email.substringAfter("@", "")) -> "Please enter a valid email."
        else -> null
    }

    fun validateQuestions(): String? = validateContact() ?: when {
        workingWithAgent == null -> "Please choose whether you are working with an agent."
        interest == null -> "Please choose what you are interested in."
        else -> null
    }

    fun toDraft(): VisitorSignInDraft = VisitorSignInDraft(
        fullName = fullName,
        phone = phone,
        email = email,
        workingWithAgent = workingWithAgent == true,
        interest = interest ?: VisitorInterest.Browsing
    )
}
